export class ArrayUtils {
  /**
   * Return new array with excluding itemToRemove
   * Orginal array is not changed
   *
   * @param  source Input Array
   * @param  removedItems items to remove
   * @param  [key=null] key to compare
   * @returns  new array
   */
  public static removeItems<T>(
    source: T[],
    removedItems: unknown[],
    key: string = null
  ): T[] {
    if (
      !source ||
      source.length === 0 ||
      !removedItems ||
      removedItems?.length === 0
    ) {
      return source;
    }

    return source.filter(item => {
      const idx = removedItems.findIndex(removedItem => {
        const itemToRemove = removedItem[key] || removedItem;
        return key ? item[key] === itemToRemove : item === removedItem;
      });
      return idx < 0;
    });
  }

  /**
   * Return new array with excluding itemToRemove
   * Orginal array is not changed
   *
   * @param  list Input Array
   * @param  removedItem Item to remove
   * @param  [key=null] key to compare
   * @returns  new array
   */
  public static removeItem<T>(list: T[], removedItem: T, key = null): T[] {
    if (!Array.isArray(list)) {
      return [];
    }

    return list.filter(item => {
      if (key) {
        return item[key] !== removedItem[key];
      } else {
        return item !== removedItem;
      }
    });
  }

  /**
   * Modify source array with excluding itemToRemove
   * Orginal array is not changed
   *
   * @param  list Input Array
   * @param  itemToRemove Item to remove
   * @param  [key=null] key to compare
   * @returns  source array
   */
  public static removeItemHard<T>(list: T[], itemToRemove: T, key = null): T[] {
    if (!Array.isArray(list)) {
      return list;
    }

    const index = list.findIndex(item => {
      if (key) {
        return item[key] !== itemToRemove[key];
      } else {
        return item !== itemToRemove;
      }
    });
    list.splice(index, 1);
    return list;
  }

  public static hasItem<T>(list: T[], item: unknown, key = null): boolean {
    if (!Array.isArray(list) || !item) {
      return false;
    }

    let result = false;
    for (const element of list) {
      if (key) {
        result = result || item[key] === element[key];
      } else {
        result = result || item === element;
      }
    }

    return result;
  }

  public static concat<T>(array1: T[] = [], array2: T[] = []): T[] {
    if (!array1 || !array2) {
      return array1 || array2;
    }

    if (!Array.isArray(array2)) {
      return array1;
    }

    try {
      return array1.concat(array2);
    } catch {
      return [];
    }
  }

  public static isEqual<T, R>(
    array1: T[] = [],
    array2: R[] = [],
    key = null
  ): boolean {
    if (!Array.isArray(array1) || !Array.isArray(array2)) {
      return false;
    }
    if (!key) {
      return JSON.stringify([...array1]) === JSON.stringify([...array2]);
    }

    const formatArray1 = array1.map(item => item[key]);
    const formatArray2 = array2.map(item => item[key]);

    return JSON.stringify(formatArray1) === JSON.stringify(formatArray2);
  }
}
